'use client'

import {
  type ICompanyLiteData,
  type ICreditSafe,
  isResponseError,
  useSession,
  useUpFetch,
} from '@repo/sdk'
import { Button, countryListWithFlags, Form, Spinner, useCompanyApi } from '@repo/ui'
import { toast } from '@repo/ui'
import { useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { CompanyFormFields } from './fields.tsx'

export const CompanySettings = () => {
  return (
    <div className={'mx-auto mb-[60px] w-[90%] max-w-[968px] py-6 dark:text-gray-300'}>
      <div className={'border-b border-b-gray-200 pb-4'}>
        <p className={'text-lg font-medium md:text-2xl'}>Your Company Information</p>
      </div>
      <CompanyForm />
    </div>
  )
}

interface FormProps {
  onCompanyExists?: () => void
  onSuccessFn?: () => void
}

export const CompanyForm = ({ onCompanyExists, onSuccessFn }: FormProps) => {
  const upFetch = useUpFetch()
  const creditSafeCallback = async (vat: string) =>
    upFetch<ICreditSafe>(`/credit-safe/companies`, {
      params: {
        countries: vat.slice(0, 2),
        vatNo: vat,
      },
    })

  const session = useSession()

  const savedVat = localStorage.getItem('vat')
  const user = session?.data?.user
  const companyData = session?.data?.user?.company
  const companyId = companyData?.id

  const form = useForm<ICompanyLiteData>({
    defaultValues: companyData,
  })

  const { control, formState, handleSubmit, reset } = form

  const { createNewCompany, updateCompany } = useCompanyApi()

  const vat = useWatch({ control, name: 'account.vat' })

  useEffect(() => {
    if (savedVat) {
      form.setValue('account.vat', savedVat)
    }
  }, [savedVat])

  useEffect(() => {
    const checkFn = async () => {
      await onCreditSafeClick()
      localStorage.removeItem('vat')
    }
    checkFn()
  }, [vat, savedVat])

  const updateCompanyMutation = useMutation({
    mutationFn: (data: ICompanyLiteData) => {
      if (companyId) {
        return updateCompany(
          {
            ...data,
            profile: { ...companyData.profile, status: companyData.profile.status.alias },
          },
          companyId,
        )
      } else if (user && user.id != null) {
        return createNewCompany(data, { ...user, id: z.coerce.number().optional().parse(user.id) })
      } else {
        return Promise.reject(new Error('Invalid data. Please get in touch with our team.'))
      }
    },
    onError: error => {
      if (isResponseError(error)) {
        console.error(error.data)
        toast.error(error.data.message, {
          description: 'Please get in touch with our team.',
        })
        return
      }
      if (onCompanyExists) {
        return onCompanyExists()
      }

      toast.error(error.message)
    },
    onSuccess: () => {
      toast.success('Company information updated successfully!')
      onSuccessFn?.()
    },
  })

  const onSubmit: SubmitHandler<ICompanyLiteData> = data => {
    if (data.account && 'vat' in data.account && 'registration_number' in data.account) {
      updateCompanyMutation.mutate({ ...data })
    }
  }

  const onCreditSafeClick = async () => {
    if (vat) {
      const trimmedVat = vat.replaceAll(' ', '')
      const response = await creditSafeCallback(trimmedVat)

      if (response.messages && response.messages!.some(message => message.code === 'NoResults')) {
        toast.error(response.messages[0].text)
        return
      }

      if (response) {
        const company = response.companies[0]
        const { city, postCode, simpleValue, street } = company.address

        reset({
          account: {
            registration_number: company.regNo,
            vat: trimmedVat,
          },
          address: {
            address: `${simpleValue.slice(0, simpleValue.indexOf(street))} ${street}`,
            city,
            country: {
              code:
                countryListWithFlags.find(
                  countryData => countryData.code === response.companies[0].country,
                )?.code ?? '',
            },
            postcode: postCode,
          },
          phone: response.companies[0].phoneNumbers[0],
          title: response.companies[0].name,
        })
      }
    }
  }

  return (
    <Form {...form}>
      <form data-hs-cf-bound="true" onSubmit={handleSubmit(onSubmit)}>
        <CompanyFormFields control={control} onCreditSafeClick={onCreditSafeClick} />
        <div className={'flex justify-end'}>
          <Button
            className={'font-semibold'}
            disabled={formState.isSubmitting || updateCompanyMutation.isPending}
            type="submit"
          >
            Save {updateCompanyMutation.isPending && <Spinner />}
          </Button>
        </div>
      </form>
    </Form>
  )
}
